/**
 * Media Permission Recovery Service
 * Generates browser-specific recovery instructions
 */

import {
  BROWSER_TYPES,
  DEVICE_TYPES,
  BROWSER_INSTRUCTIONS,
  BLOCKED_MESSAGES,
  getInstructionAssetPath,
  isPWA,
} from './permissionConstants'
import { detectBrowser, detectDeviceType, detectOS } from './permissionDetector'

/**
 * Get recovery instructions based on browser and device
 */
export const getRecoveryInstructions = (mediaType = 'both') => {
  const browser = detectBrowser()
  const deviceType = detectDeviceType()
  const pwaMode = isPWA()

  // Determine if mobile or desktop
  const isMobile = deviceType === DEVICE_TYPES.MOBILE || deviceType === DEVICE_TYPES.TABLET
  const platform = isMobile ? 'mobile' : 'desktop'

  // Get base instructions for the browser
  let instructions = []
  
  // Safari PWA has special instructions
  if (browser === BROWSER_TYPES.SAFARI && pwaMode && isMobile) {
    instructions = BROWSER_INSTRUCTIONS[BROWSER_TYPES.SAFARI].pwa || []
  } else if (BROWSER_INSTRUCTIONS[browser]) {
    instructions = BROWSER_INSTRUCTIONS[browser][platform] || BROWSER_INSTRUCTIONS[browser].desktop || []
  } else {
    // Generic fallback instructions
    instructions = [
      'Open your browser settings',
      'Find site permissions or privacy settings',
      'Locate this website in the list',
      'Enable Camera and Microphone permissions',
      'Refresh this page',
    ]
  }

  return instructions
}

/**
 * Get blocked message based on media type
 */
export const getBlockedMessage = (mediaType = 'both') => {
  const messageKey = mediaType.toUpperCase()
  return BLOCKED_MESSAGES[messageKey] || BLOCKED_MESSAGES.BOTH
}

/**
 * Get visual asset path for instructions
 */
export const getInstructionVisuals = (actionType = 'unblock') => {
  const browser = detectBrowser()
  const deviceType = detectDeviceType()
  const pwaMode = isPWA()

  const isMobile = deviceType === DEVICE_TYPES.MOBILE || deviceType === DEVICE_TYPES.TABLET
  const platform = isMobile ? 'mobile' : 'desktop'

  // For Safari PWA, use special assets
  if (browser === BROWSER_TYPES.SAFARI && pwaMode && isMobile) {
    return [
      {
        src: getInstructionAssetPath('safari', 'pwa', 'system-permissions.gif'),
        alt: 'Open iOS Settings and enable permissions for AUMHUM app',
        caption: 'Open iOS Settings → AUMHUM → Enable Camera & Microphone',
      },
    ]
  }

  // Return appropriate visuals based on browser
  const visuals = []

  switch (browser) {
    case BROWSER_TYPES.CHROME:
    case BROWSER_TYPES.EDGE:
      visuals.push({
        src: getInstructionAssetPath(browser, platform, `${actionType}-from-settings.gif`),
        alt: `How to ${actionType} camera and microphone in ${browser}`,
        caption: 'Click the lock icon in the address bar to manage permissions',
      })
      break

    case BROWSER_TYPES.SAFARI:
      if (isMobile) {
        visuals.push({
          src: getInstructionAssetPath('safari', 'ios', 'settings.gif'),
          alt: 'How to enable camera and microphone in iOS Safari',
          caption: 'Open iOS Settings → Safari → Camera & Microphone',
        })
      } else {
        visuals.push({
          src: getInstructionAssetPath('safari', 'desktop', 'website-settings.gif'),
          alt: 'How to enable camera and microphone in Safari',
          caption: 'Safari menu → Settings for This Website',
        })
      }
      break

    case BROWSER_TYPES.FIREFOX:
      visuals.push({
        src: getInstructionAssetPath('firefox', platform, 'permissions.gif'),
        alt: 'How to enable camera and microphone in Firefox',
        caption: 'Click the camera/microphone icon in the address bar',
      })
      break

    default:
      // Generic visual
      visuals.push({
        src: getInstructionAssetPath('generic', platform, 'permissions.gif'),
        alt: 'How to enable camera and microphone permissions',
        caption: 'Check your browser settings to enable permissions',
      })
  }

  return visuals
}

/**
 * Get quick help text for address bar icon
 */
export const getQuickHelp = () => {
  const browser = detectBrowser()
  const deviceType = detectDeviceType()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE || deviceType === DEVICE_TYPES.TABLET

  if (isMobile) {
    switch (browser) {
      case BROWSER_TYPES.CHROME:
      case BROWSER_TYPES.EDGE:
        return 'Tap the three dots menu (⋮) → Settings → Site settings'
      case BROWSER_TYPES.SAFARI:
        return 'Open iOS Settings → Safari → Camera/Microphone'
      case BROWSER_TYPES.FIREFOX:
        return 'Tap the lock icon → Edit Site Settings'
      default:
        return 'Check your browser menu for site settings'
    }
  }

  switch (browser) {
    case BROWSER_TYPES.CHROME:
    case BROWSER_TYPES.EDGE:
      return 'Click the lock icon (🔒) in the address bar'
    case BROWSER_TYPES.SAFARI:
      return 'Safari menu → Settings for This Website'
    case BROWSER_TYPES.FIREFOX:
      return 'Click the camera/microphone icon in the address bar'
    default:
      return 'Check your browser settings for site permissions'
  }
}

/**
 * Get browser-specific help URL
 */
export const getBrowserHelpURL = () => {
  const browser = detectBrowser()
  const os = detectOS()

  const helpURLs = {
    [BROWSER_TYPES.CHROME]: 'https://support.google.com/chrome/answer/2693767',
    [BROWSER_TYPES.SAFARI]:
      os === 'ios'
        ? 'https://support.apple.com/guide/iphone/control-access-to-hardware-features-iph6b3d24c1e/ios'
        : 'https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac',
    [BROWSER_TYPES.FIREFOX]: 'https://support.mozilla.org/kb/how-manage-your-camera-and-microphone-permissions',
    [BROWSER_TYPES.EDGE]: 'https://support.microsoft.com/microsoft-edge/use-your-camera-in-microsoft-edge-6d0075e6-2fa5-4f35-a23b-c3a36f13eeda',
  }

  return helpURLs[browser] || 'https://support.google.com/chrome/answer/2693767'
}

/**
 * Generate complete recovery plan
 */
export const generateRecoveryPlan = (mediaType = 'both', includeVisuals = true) => {
  const browser = detectBrowser()
  const deviceType = detectDeviceType()
  const os = detectOS()
  const pwaMode = isPWA()

  const instructions = getRecoveryInstructions(mediaType)
  const blockedMessage = getBlockedMessage(mediaType)
  const quickHelp = getQuickHelp()
  const helpURL = getBrowserHelpURL()
  const visuals = includeVisuals ? getInstructionVisuals('unblock') : []

  return {
    browser,
    deviceType,
    os,
    isPWA: pwaMode,
    mediaType,
    message: blockedMessage,
    quickHelp,
    instructions,
    visuals,
    helpURL,
    canAutoRetry: browser !== BROWSER_TYPES.SAFARI, // Safari requires manual refresh
  }
}

/**
 * Check if we should show simplified instructions
 * (e.g., for known browsers on desktop)
 */
export const shouldUseSimplifiedInstructions = () => {
  const browser = detectBrowser()
  const deviceType = detectDeviceType()

  // Desktop Chrome/Edge have the simplest flow
  return (
    deviceType === DEVICE_TYPES.DESKTOP &&
    (browser === BROWSER_TYPES.CHROME || browser === BROWSER_TYPES.EDGE)
  )
}

/**
 * Get encouragement message based on attempt count
 */
export const getEncouragementMessage = (attemptCount = 0) => {
  const messages = [
    "Don't worry, this is a common issue and easy to fix!",
    "You're almost there! Just a few more steps.",
    "We're here to help. Follow the steps above carefully.",
    "Having trouble? Try refreshing the page after changing settings.",
    "Still stuck? Our support team is here to help.",
  ]

  return messages[Math.min(attemptCount, messages.length - 1)]
}

/**
 * Check if browser requires page refresh after permission change
 */
export const requiresPageRefresh = () => {
  const browser = detectBrowser()
  
  // Safari typically requires a refresh
  return browser === BROWSER_TYPES.SAFARI
}

/**
 * Get success message after permissions granted
 */
export const getSuccessMessage = (mediaType = 'both') => {
  const messages = {
    both: "Great! Camera and microphone access enabled. You're ready to join the call.",
    camera: "Great! Camera access enabled. You're ready to share your video.",
    microphone: "Great! Microphone access enabled. You're ready to speak in the call.",
  }

  return messages[mediaType] || messages.both
}

